import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CheckSquare, RefreshCw, Save, SearchCheck, Square } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

export default function MissingSalesRadar() {
  const { t } = useTranslation();
  const [stores, setStores] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const applyPayload = useCallback((payload) => {
    const nextStores = Array.isArray(payload) ? payload : payload?.stores || [];
    const selected = Array.isArray(payload?.selected_store_ids)
      ? payload.selected_store_ids
      : nextStores
          .filter((store) => store.selected ?? store.monitored ?? store.enabled)
          .map((store) => store.id ?? store.store_id);
    setStores(nextStores);
    setSelectedIds(new Set(selected.map(String)));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/missing-sales/radar");
      applyPayload(data);
    } catch (requestError) {
      setError(formatApiError(requestError.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [applyPayload]);

  useEffect(() => {
    load();
  }, [load]);

  const sortedStores = useMemo(
    () => [...stores].sort((a, b) => storeName(a).localeCompare(storeName(b))),
    [stores]
  );

  const toggleStore = (storeId) => {
    const id = String(storeId);
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/missing-sales/radar", {
        store_ids: Array.from(selectedIds),
      });
      if (data) applyPayload(data);
      toast.success(t("missingSales.radar.saved"));
    } catch (requestError) {
      toast.error(formatApiError(requestError.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="pg-card p-10 text-center text-slate-400">{t("common.loading")}</div>;
  }

  if (error) {
    return (
      <div className="pg-card p-8 text-center" data-testid="missing-sales-radar-error">
        <h1 className="text-xl font-bold text-slate-900">{t("missingSales.radar.loadFailed")}</h1>
        <p className="text-sm text-slate-500 mt-2">{error}</p>
        <button
          type="button"
          onClick={load}
          className="mt-6 px-4 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-500 text-white font-semibold text-sm inline-flex items-center gap-2"
        >
          <RefreshCw size={15} /> {t("common.refresh")}
        </button>
      </div>
    );
  }

  return (
    <div data-testid="missing-sales-radar-page">
      <div className="flex items-end justify-between mb-7 flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.32em] text-slate-400 mb-1">
            {t("missingSales.breadcrumb")}
          </div>
          <h1 className="text-3xl font-extrabold text-slate-900">
            {t("missingSales.radar.title")}
          </h1>
          <p className="text-sm text-slate-500 mt-2 max-w-2xl">
            {t("missingSales.radar.description")}
          </p>
        </div>
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="px-4 py-2.5 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-semibold text-sm flex items-center gap-2 disabled:opacity-60"
          data-testid="save-missing-sales-radar"
        >
          <Save size={16} />
          {saving ? t("missingSales.radar.saving") : t("missingSales.radar.save")}
        </button>
      </div>

      <section className="pg-card overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-cyan-50 text-cyan-700 flex items-center justify-center">
              <SearchCheck size={18} />
            </div>
            <div>
              <h2 className="font-bold text-slate-900">{t("missingSales.radar.availableStores")}</h2>
              <p className="text-xs text-slate-500 mt-0.5">
                {t("missingSales.radar.selectedCount", { count: selectedIds.size })}
              </p>
            </div>
          </div>
          {stores.length > 0 && (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setSelectedIds(new Set(stores.map((store) => String(store.id ?? store.store_id))))}
                className="px-3 py-2 rounded-md border border-slate-200 text-slate-700 bg-white hover:bg-slate-50 text-xs font-semibold flex items-center gap-1.5"
              >
                <CheckSquare size={14} /> {t("missingSales.radar.selectAll")}
              </button>
              <button
                type="button"
                onClick={() => setSelectedIds(new Set())}
                className="px-3 py-2 rounded-md border border-slate-200 text-slate-700 bg-white hover:bg-slate-50 text-xs font-semibold flex items-center gap-1.5"
              >
                <Square size={14} /> {t("missingSales.radar.clearAll")}
              </button>
            </div>
          )}
        </div>

        {sortedStores.length === 0 ? (
          <div className="px-5 py-12 text-center text-slate-400">
            {t("missingSales.radar.empty")}
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {sortedStores.map((store) => {
              const id = String(store.id ?? store.store_id);
              const selected = selectedIds.has(id);
              return (
                <label
                  key={id}
                  className="px-5 py-4 flex items-center justify-between gap-4 hover:bg-slate-50/70 cursor-pointer"
                  data-testid={`missing-sales-radar-store-${id}`}
                >
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-800 truncate">{storeName(store)}</div>
                    <div className="text-xs text-slate-400 mt-0.5 mono">
                      MID {store.machine_id || "—"}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className={`pill ${selected ? "pill-success" : "pill-warning"}`}>
                      {selected
                        ? t("missingSales.radar.included")
                        : t("missingSales.radar.notIncluded")}
                    </span>
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => toggleStore(id)}
                      className="w-5 h-5 rounded border-slate-300 text-cyan-600 focus:ring-cyan-500"
                      aria-label={t("missingSales.radar.toggleStore", { name: storeName(store) })}
                    />
                  </div>
                </label>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}

function storeName(store) {
  return store.name || store.store_name || store.uid || "—";
}
